import React from 'react';

export type TooltipProps = {
  /** Whether to render the tooltip. When false, renders null. */
  visible?: boolean;
  /** Mouse/client X position where tooltip should appear. */
  x?: number | null;
  /** Mouse/client Y position where tooltip should appear. */
  y?: number | null;
  /** Offset in px from the anchor point. Either a number (applies to both x and y) or per-axis. */
  offset?: number | { x?: number; y?: number };
  /** Additional classes for the tooltip bubble container. */
  className?: string;
  /** Use fixed (default) or absolute positioning. */
  position?: 'fixed' | 'absolute';
  /** Tooltip content. */
  children: React.ReactNode;
};

/**
 * Tooltip
 *
 * A small reusable bubble positioned near the given (x, y) coordinate. Styling matches
 * the existing schedule tooltip by default. Intended to be used together with
 * `useHoverTooltip` hook but can be used with any coordinates.
 */
export default function Tooltip({
  visible = false,
  x = null,
  y = null,
  offset = 12,
  className = '',
  position = 'fixed',
  children,
}: TooltipProps) {
  if (!visible || x == null || y == null) return null;

  const offX = typeof offset === 'number' ? offset : offset?.x ?? 0;
  const offY = typeof offset === 'number' ? offset : offset?.y ?? 0;

  const style: React.CSSProperties = {
    left: x + offX,
    top: y + offY,
  };

  const base =
    'z-50 transition-opacity duration-300 opacity-100 px-3 py-1 bg-gray-700 text-white text-xs rounded-full shadow-xl pointer-events-none';

  return (
    <div className={`${position} ${base} ${className}`} style={style}>
      {children}
    </div>
  );
}
